import { useState } from "react";
import { SALES_DEFINITIONS } from "./salesDefinitions.ts";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { ItemSales } from "./salesBreakdowns.ts";
import { relativeChange } from "./salesSummary.ts";
import {
  formatChange,
  formatCount,
  formatEuros,
  formatPercent,
} from "./salesFormat.ts";
import { SalesPeriod } from "./salesPeriod.ts";
import { SaleRecord, SalesGateway } from "./SaleRecord.ts";
import { EmptyStatistics, LoadingBlock } from "./StatisticsCard.tsx";
import { StatisticsCardWithDetails } from "./StatisticsCardWithDetails.tsx";
import { soldToCustomerInCents } from "./salesCustomers.ts";
import {
  CUSTOMER_IDENTITY_COLUMNS,
  CUSTOMER_LAST_ORDER_DATE_COLUMN,
  CUSTOMER_ORDER_COUNT_COLUMN,
  CUSTOMER_SOLD_COLUMN,
  customerItemsColumn,
  ORDER_AMOUNT_COLUMN,
  ORDER_CUSTOMER_COLUMNS,
  ORDER_DATE_COLUMN,
  ORDER_GATEWAY_COLUMN,
  ORDER_ID_COLUMN,
  ORDER_PAYMENT_METHOD_COLUMN,
  orderItemColumn,
} from "./salesDetailsColumns.tsx";
import { OrdersLinksMenu } from "./OrdersLinksMenu.tsx";
import { ordersListPathFor } from "./ordersListLinks.ts";
import {
  centsForCsv,
  downloadSalesCsv,
  ratioForCsv,
  toCsv,
} from "./salesCsv.ts";

const ITEMS_SHOWN_AT_FIRST = 10;
const SHARE_BAR_COLOR = "#2a78d6";

function ChangeOfItem({
  current,
  previous,
}: {
  current: number;
  previous: number;
}) {
  const change = relativeChange(current, previous);
  if (change === null)
    return <span className="text-xs text-gray-400">Nuovo</span>;
  if (change === 0) return <span className="text-xs text-gray-400">=</span>;
  const Arrow = change > 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      title={`Periodo di confronto: ${formatEuros(previous)}`}
      className={`inline-flex items-center gap-0.5 text-xs font-medium ${change > 0 ? "text-emerald-700" : "text-red-700"}`}
    >
      <Arrow className="w-3.5 h-3.5" aria-hidden />
      {formatChange(change)}
    </span>
  );
}

export function TopItemsCard({
  items,
  sales,
  previousSoldByItem,
  comparisonShortLabel,
  itemLabelOf,
  period,
  isLoading,
}: {
  items: ItemSales[];
  sales: SaleRecord[];
  previousSoldByItem: Map<string, number> | null;
  comparisonShortLabel: string;
  itemLabelOf: (itemKey: string) => string;
  period: SalesPeriod;
  isLoading: boolean;
}) {
  const [isShowingAll, setIsShowingAll] = useState(false);
  const shownItems = isShowingAll
    ? items
    : items.slice(0, ITEMS_SHOWN_AT_FIRST);
  const previousSoldOf = (item: ItemSales) =>
    previousSoldByItem?.get(item.itemKey) ?? 0;

  const exportCsv = () =>
    downloadSalesCsv({
      section: "prodotti",
      period,
      csv: toCsv(items, [
        {
          header: "Prodotto o bundle",
          valueOf: (item) => itemLabelOf(item.itemKey),
        },
        { header: "ID", valueOf: (item) => item.itemKey },
        { header: "Ordini", valueOf: (item) => item.orderCount },
        {
          header: "Venduto (€)",
          valueOf: (item) => centsForCsv(item.soldInCents),
        },
        ...(previousSoldByItem
          ? [
              {
                header: "Venduto periodo di confronto (€)",
                valueOf: (item: ItemSales) => centsForCsv(previousSoldOf(item)),
              },
              {
                header: "Variazione (%)",
                valueOf: (item: ItemSales) => {
                  const change = relativeChange(
                    item.soldInCents,
                    previousSoldOf(item),
                  );
                  return change === null ? null : ratioForCsv(change);
                },
              },
            ]
          : []),
        {
          header: "Quota (%)",
          valueOf: (item) => ratioForCsv(item.shareOfSold),
        },
      ]),
    });

  return (
    <StatisticsCardWithDetails
      title="Prodotti e bundle più venduti"
      info={SALES_DEFINITIONS.topItems}
      description="Ordini conclusi nel periodo, dal più venduto"
      csvSection="prodotti"
      period={period}
      isLoading={isLoading}
      exportSummaryCsv={items.length > 0 ? exportCsv : undefined}
      orders={sales}
      orderColumns={[
        ORDER_ID_COLUMN,
        ORDER_DATE_COLUMN,
        ...ORDER_CUSTOMER_COLUMNS,
        orderItemColumn(itemLabelOf),
        ORDER_GATEWAY_COLUMN,
        ORDER_PAYMENT_METHOD_COLUMN,
        ORDER_AMOUNT_COLUMN,
      ]}
      customerColumns={[
        ...CUSTOMER_IDENTITY_COLUMNS,
        CUSTOMER_ORDER_COUNT_COLUMN,
        customerItemsColumn(itemLabelOf),
        CUSTOMER_SOLD_COLUMN,
        CUSTOMER_LAST_ORDER_DATE_COLUMN,
      ]}
      rankCustomersBy={soldToCustomerInCents}
      noOrdersMessage="Nessun prodotto venduto nel periodo"
      summary={
        isLoading ? (
          <LoadingBlock className="h-64 w-full" />
        ) : items.length === 0 ? (
          <EmptyStatistics>Nessun prodotto venduto nel periodo</EmptyStatistics>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-3 font-medium">Prodotto o bundle</th>
                    <th className="py-2 pr-3 font-medium text-right">Ordini</th>
                    <th className="py-2 pr-3 font-medium text-right">
                      Venduto
                    </th>
                    {previousSoldByItem && (
                      <th className="py-2 pr-3 font-medium text-right whitespace-nowrap">
                        {comparisonShortLabel}
                      </th>
                    )}
                    <th className="py-2 font-medium w-28">Quota</th>
                  </tr>
                </thead>
                <tbody>
                  {shownItems.map((item) => (
                    <tr
                      key={item.itemKey}
                      className="border-b border-gray-100 text-gray-700"
                    >
                      <td className="py-2 pr-3 min-w-40">
                        <div className="flex items-start gap-1">
                          <span className="break-words">
                            {itemLabelOf(item.itemKey)}
                          </span>
                          <OrdersLinksMenu
                            label="Vedi gli ordini del periodo"
                            links={Object.entries(item.orderCountByGateway).map(
                              ([gateway, orderCount]) => ({
                                gateway: gateway as SalesGateway,
                                orderCount: orderCount ?? 0,
                                path: ordersListPathFor({
                                  gateway: gateway as SalesGateway,
                                  itemKey: item.itemKey,
                                  period,
                                }),
                              }),
                            )}
                          />
                        </div>
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {formatCount(item.orderCount)}
                      </td>
                      <td className="py-2 pr-3 text-right whitespace-nowrap">
                        {formatEuros(item.soldInCents)}
                      </td>
                      {previousSoldByItem && (
                        <td className="py-2 pr-3 text-right whitespace-nowrap">
                          <ChangeOfItem
                            current={item.soldInCents}
                            previous={previousSoldOf(item)}
                          />
                        </td>
                      )}
                      <td className="py-2">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 flex-1 rounded-full bg-gray-100">
                            <div
                              className="h-1.5 rounded-full"
                              style={{
                                width: `${item.shareOfSold * 100}%`,
                                backgroundColor: SHARE_BAR_COLOR,
                              }}
                            />
                          </div>
                          <span className="w-11 text-right text-xs text-gray-500">
                            {formatPercent(item.shareOfSold)}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {items.length > ITEMS_SHOWN_AT_FIRST && (
              <button
                type="button"
                onClick={() => setIsShowingAll((isShowing) => !isShowing)}
                className="self-start text-xs text-blue-600 hover:underline"
              >
                {isShowingAll
                  ? "Mostra solo i primi 10"
                  : `Mostra tutti (${items.length})`}
              </button>
            )}
          </div>
        )
      }
    />
  );
}
